// Amend PO form model (POS comments item 10): rows from an approved PO, the
// rules each row must keep, and the payload for PUT /purchase-orders/:id/amend.
//
// An existing row keeps its line id — receipts and vendor bills point at it —
// so the API changes it in place. A removed row is only allowed while nothing
// of it was received; a partly received line is closed short instead.

import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { computePoLine, toPoLineInput } from '@/features/purchase-orders/line-totals';
import type { AmendPoLinePayload, PoDiscountType, PurchaseOrder } from '@/features/purchase-orders';

export interface AmendRow {
  key: string;
  /** Existing PO line id; absent on an added row. */
  id?: string;
  product_id?: string;
  ingredient_id?: string;
  name: string;
  received: number;
  closed: boolean;
  removed: boolean;
  quantity: string;
  unit: string;
  unit_cost: string;
  gst_rate: string;
  discount_type: PoDiscountType;
  discount_value: string;
}

export function rowsFromPo(po: Pick<PurchaseOrder, 'items'>, currency: string): AmendRow[] {
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  return po.items.map((i) => ({
    key: i.id,
    id: i.id,
    name: i.product_name,
    received: i.received_quantity,
    closed: i.status === 'CLOSED',
    removed: false,
    quantity: String(i.quantity),
    unit: i.unit,
    unit_cost: (i.unit_cost / subunit).toFixed(dp),
    gst_rate: String(i.gst_rate ?? 0),
    discount_type: i.discount_type ?? 'FLAT',
    discount_value: i.discount_value
      ? String(i.discount_type === 'PERCENTAGE' ? i.discount_value / 100 : i.discount_value / subunit)
      : '',
  }));
}

/** Why a row cannot be saved as typed, or null. */
export function rowError(row: AmendRow): string | null {
  if (row.removed) return row.received > 0 ? 'Already received — close the rest instead of removing it' : null;
  const qty = parseFloat(row.quantity);
  if (!(qty > 0)) return 'Enter a quantity';
  if (qty + 1e-9 < row.received) return `Cannot go below the ${row.received} already received`;
  if (!(parseFloat(row.unit_cost) > 0)) return 'Enter a unit cost';
  if (!row.id && !row.product_id && !row.ingredient_id) return 'Pick a product';
  return null;
}

/** `currency` is the PO's (unit scaling); `tenantCurrency` picks the tax regime. */
export function amendTotal(rows: AmendRow[], currency: string, tenantCurrency?: string): number {
  return rows
    .filter((r) => !r.removed)
    .reduce((sum, r) => sum + computePoLine(toPoLineInput(r, currency), tenantCurrency).line_total, 0);
}

export function buildAmendPayload(rows: AmendRow[], currency: string): AmendPoLinePayload[] {
  return rows
    .filter((r) => !r.removed)
    .map((r) => {
      const line = toPoLineInput(r, currency);
      return {
        ...(r.id ? { id: r.id } : r.ingredient_id ? { ingredient_id: r.ingredient_id } : { product_id: r.product_id }),
        quantity: line.quantity,
        unit: r.unit,
        unit_cost: line.unit_cost,
        gst_rate: line.gst_rate,
        discount_type: line.discount_type,
        discount_value: line.discount_value,
      };
    });
}

/** True when the form differs from the PO it was loaded from. */
export function amendChanged(rows: AmendRow[], original: AmendRow[]): boolean {
  if (rows.some((r) => !r.id || r.removed)) return true;
  const strip = ({ key: _k, removed: _r, ...rest }: AmendRow) => JSON.stringify(rest);
  return rows.some((r) => strip(r) !== strip(original.find((o) => o.id === r.id) ?? r));
}
